import styled from "@emotion/styled";
import { Box, Collapse, Grid, IconButton, Table } from "@mui/material";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { ReactComponent as AddUser } from "assets/AddUser.svg";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as SortIcon } from "assets/SortIcon.svg";

import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import { ReactComponent as ChatBubbleIcon } from "assets/mail-chat-bubble-square-warning.svg";
import { setSelectedUserList } from "components/features/Corp/CorpUser.Slice";
import { getAllBreakupLists } from "components/features/Corp/MyLists/MyLists";
import { setProgressInfoSummary } from "components/features/Progress/Progress.slice";
import { isNull, uniqueId } from "lodash";
import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { abovePropertyViewTypeProperty } from "utils/CommonConstants";
import { getTableColor, getUserNameByHltId } from "utils/CommonFunctions";
import { getUIFormatDate } from "utils/DateHelper";
import BookmarkPlan from "./Bookmark";
import Button from "./Button";
import CheckBox from "./Checkbox";
import Scroller from "./Scroller/InfiniteScroller";
import "./Table.scss";
import Typography from "./Typography";

export const StyledLink = styled(Link)`
  font-family: Noto Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  text-decoration: none;
  color: ${tokens.Text.General.PrimaryHeadline};
`;

function Row(props: any) {
  const { row } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const isTooltipInTable = row?.data?.find((item: any) => !!item.tooltip);

  const renderList = (item: any, isExpanded: boolean) => {
    return isExpanded && item.tooltip?.list?.length && item.content !== 0
      ? item?.tooltip?.list?.map((item: any) => {
          return (
            <Grid
              key={uniqueId("TOOLTIP_ITEM_")}
              sx={{ paddingY: "4px", color: getTableColor(item?.name), whiteSpace: "nowrap" }}
            >
              <Typography variant={"body2"} component={"span"} showBold>
                {item.value < 10 ? "0" : ""}
                {`${item.value ?? ""}  `}
              </Typography>
              {item?.name} {item?.isShowStar > 0 ? "*" : ""}
            </Grid>
          );
        })
      : item.content ?? "-";
  };

  return (
    <TableRow key={row.id} sx={{ "&:last-child td": { border: 0 } }} className="table-row-common">
      {isTooltipInTable && (
        <TableCell
          component="td"
          scope="row"
          sx={{
            padding: "8px 0 8px 8px",
            width: "12px",
            color: tokens.Text.General.PrimaryHeadline,
            cursor: "pointer"
          }}
        >
          <IconButton onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? <ArrowUp /> : <ArrowDown />}</IconButton>
        </TableCell>
      )}
      {row?.data?.map((item: any) => {
        return (
          <TableCell
            key={uniqueId("TABLECELL_")}
            component="td"
            scope="row"
            sx={{
              padding: "8px 16px",
              color: tokens.Text.General.PrimaryHeadline
            }}
          >
            {item?.to ? (
              <StyledLink to={`${item?.to}`}>{renderList(item, isExpanded)}</StyledLink>
            ) : (
              <Typography component={"div"} variant={"body2"} showBold={item.showBold} color={item.color}>
                {renderList(item, isExpanded)}
              </Typography>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function SCRow(props: any) {
  const { row } = props;
  const [open, setOpen] = React.useState<boolean>(false);
  const bookmarkedProperties = useAppSelector((state) => state.aboveProperty.bookmarkedProperties);
  const userInfo = getUserNameByHltId(props.allTeamMember, row.inncodeMappingUserName);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  let renderListInfo;
  if (open && row.implementedPlansCountStatusBreakUp) {
    let implementedPlansCountStatusBreakUp = getAllBreakupLists(row.implementedPlansCountStatusBreakUp);
    const renderList = () => {
      return implementedPlansCountStatusBreakUp?.map((item: any) => {
        return (
          <Grid
            key={uniqueId("TOOLTIP_ITEM_")}
            sx={{ paddingY: "4px", color: getTableColor(item?.name), whiteSpace: "nowrap" }}
          >
            <Typography variant={"body2"} component={"span"} showBold>
              {item.value < 10 ? "0" : ""}
              {`${item.value ?? ""}  `}
            </Typography>
            {item?.name} {item?.isShowStar > 0 ? "*" : ""}
          </Grid>
        );
      });
    };
    renderListInfo = renderList();
  }

  return (
    <React.Fragment key={uniqueId(`sc-row-${props.index}`)}>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }} className="sc-table-top-row">
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              if (row.inncodeMappingUserName) {
                setOpen(!open);
                props.setId(row);
                !open && props.fetchIndividualProperty(row, props.index);
              }
            }}
          >
            {open ? <ArrowUp /> : <ArrowDown />}
          </IconButton>
        </TableCell>

        <TableCell component="th" scope="row" className="checkbox-row">
          <CheckBox checked={row.isChecked} onChange={(e: any) => props.handleParentChange(e, row, props.index)} />
          <Typography
            variant="body2"
            showBold
            color="#312F2E"
            className={row?.implementedPlansCount > 0 ? "cursor-pointer" : ""}
            onClick={() => {
              if (row?.implementedPlansCount > 0) {
                navigate(`/progressInfo/property/${props.selectedList?.id}?inncode=${row?.inncode}`);
                dispatch(setProgressInfoSummary(row));
              }
            }}
          >
            {row.propertyName}
          </Typography>
        </TableCell>
        <TableCell align="left" sx={{ verticalAlign: "top" }}>
          <Typography variant="body2" color="#312F2E">
            {row.inncode}
          </Typography>
        </TableCell>
        <TableCell align="left" size="medium" sx={{ verticalAlign: "top" }}>
          {row.inncodeusers?.length ? (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div
                style={{
                  height: "32px",
                  padding: "0px 8px",
                  background: !open ? "#DBEDFF" : "#F6F5F3",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <Typography color={!open ? "#004C97" : "#897D74"} variant="body2" className="no-wrap-white-space">
                  {userInfo?.displayName}
                </Typography>
              </div>
              {!open && row.inncodeusers?.length > 1 && (
                <div>
                  <Typography variant="body2" color="#312F2E">
                    (+{row.inncodeusers?.length - 1})
                  </Typography>
                </div>
              )}
            </div>
          ) : (
            <div className="add-member">
              <div
                style={{
                  background: "white",
                  height: "34px",
                  width: "max-content",
                  display: "flex",
                  alignItems: "flex-start",
                  borderRadius: "200px",
                  cursor: "pointer"
                }}
                onClick={() => props.handleUserVisible({ isVisible: true, row: row, index: props.index })}
              >
                <Typography variant="label" showBold color="#004C97">
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <AddUser /> Add Team Member
                  </div>
                </Typography>
              </div>
            </div>
          )}
        </TableCell>
        <TableCell align="left" sx={{ verticalAlign: "top" }}>
          <Typography variant="body2" color="#312F2E">
            {row.brandName}
          </Typography>
        </TableCell>
        <TableCell align="left" sx={{ verticalAlign: "top" }}>
          {!open ? (
            <Typography variant="body2" color="#312F2E">
              {row.implementedPlansCount}
            </Typography>
          ) : (
            renderListInfo
          )}
        </TableCell>
        <TableCell align="left" sx={{ verticalAlign: "top" }}>
          <BookmarkPlan
            aboveUserPayload={{
              isAboveUsertype: true,
              viewType: abovePropertyViewTypeProperty,
              inncode: row?.inncode
            }}
            isBookmarked={bookmarkedProperties?.some((b: any) => b?.innCode === row?.inncode)}
          />
        </TableCell>
      </TableRow>
      <TableRow className="sc-subtable-bottom">
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={6}
          sx={{ background: "#F6F5F3" }}
          className="parent-table-cell"
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#3E3A39">
                        Last Assignment
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#3E3A39">
                        Active Plans
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#3E3A39">
                        Completed Plans
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#3E3A39">
                        Engagement
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.history?.map((historyRow: any, historyIndex: number) => {
                    const userInfo = getUserNameByHltId(props.allTeamMember, historyRow.hltId);
                    const userClickable =
                      row?.implementedPlansCount > 0 &&
                      (historyRow?.completedPlansCount > 0 || historyRow?.activePlansCount > 0);
                    return (
                      <TableRow key={uniqueId(`${row.propertyName}-${historyRow.innCode}`)}>
                        <TableCell className="hilton-id-cell" sx={{ display: "flex", alignItems: "center" }}>
                          <CheckBox
                            onChange={() => props.handleChildChange(props.index, historyIndex)}
                            checked={historyRow.isChecked}
                          />
                          <div
                            style={{
                              height: "32px",
                              padding: "0px 8px",
                              background: "#DBEDFF",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center"
                            }}
                          >
                            <Typography
                              color="#004C97"
                              variant="body2"
                              className={userClickable ? "cursor-pointer no-wrap-white-space" : " no-wrap-white-space"}
                              onClick={() => {
                                if (userClickable) {
                                  const userData = { ...historyRow, displayName: userInfo?.displayName };
                                  navigate(
                                    `/progressInfo/users/${props.selectedList?.id}?inncode=${row?.inncode}&users=${historyRow.hltId}`
                                  );
                                  dispatch(setProgressInfoSummary(userData));
                                }
                              }}
                            >
                              {userInfo?.displayName}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="#312F2E">
                            {isNull(historyRow.lastAssignmentDateTime)
                              ? "-"
                              : getUIFormatDate(historyRow.lastAssignmentDateTime)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="#312F2E">
                            {historyRow.activePlansCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="#312F2E">
                            {historyRow.completedPlansCount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="#312F2E">
                            {isNull(historyRow.engagement) ? "-" : historyRow.engagement}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell>
                      <div className="add-member">
                        <div
                          style={{
                            background: "white",
                            padding: "0px 16px",
                            height: "34px",
                            width: "max-content",
                            display: "flex",
                            alignItems: "center",
                            borderRadius: "200px",
                            cursor: "pointer"
                          }}
                          onClick={() => props.handleUserVisible({ isVisible: true, row: row, index: props.index })}
                        >
                          <Typography variant="label" showBold color="#004C97">
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <AddUser /> Add Team Member
                            </div>
                          </Typography>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
        {open && <TableCell className="adasadasd"></TableCell>}
      </TableRow>
    </React.Fragment>
  );
}

export default function BasicTable(props: any) {
  const dispatch = useAppDispatch();
  const {
    isTablePaginated = true,
    noDataIcon = <ChatBubbleIcon />,
    noDataMessage = "There is no data",
    scrollableTarget = "tableDiv",
    sx = {},
    handleUserVisible,
    fetchIndividualProperty
  } = props;
  const fetchMore = () => {
    props?.pagination?.setPageNo(props?.pagination?.pageNo + 1);
  };

  const getSortIcon = useCallback(
    (content: string) => {
      return (
        <div
          style={{ display: "flex", alignSelf: "center" }}
          className={props?.data?.rows?.length > 0 ? "cursor-pointer" : ""}
          onClick={() => {
            if (props?.data?.rows?.length) {
              props.handleSorting(content);
            }
          }}
        >
          <SortIcon />
        </div>
      );
    },
    [props?.data?.rows?.length, props.handleSorting]
  );
  const wrapper: any = document.getElementsByClassName("my-list-corp-right-wrapper")[0];
  const tableTotalWidth = wrapper?.offsetWidth;

  const noDataFound = () => {
    return (
      <TableRow>
        <TableCell colSpan={props?.data?.headers?.length}>
          <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ padding: "40px 24px" }}>
            {noDataIcon}
            <Typography variant="body1" color={tokens.Text.General.Tertiary}>
              {noDataMessage}
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  const getScableContent = () => {
    if (props?.data?.listCategory && props?.view === "myList") {
      if (props?.data?.extraData.length) {
        return props?.data?.extraData.map((row: any, index: any) => {
          return (
            <SCRow
              key={`sc-table-${row.propertyName}`}
              row={row}
              setId={props?.setId}
              handleUserVisible={handleUserVisible}
              index={index}
              fetchIndividualProperty={fetchIndividualProperty}
              handleParentChange={props.handleParentChange}
              handleChildChange={props.handleChildChange}
              allTeamMember={props.allTeamMember}
              selectedList={props.selectedList}
            />
          );
        });
      } else {
        return noDataFound();
      }
    } else {
      if (props?.data?.rows?.length) {
        return props?.data?.rows?.map((row: any, index: any) => {
          return <Row key={row.name} row={row} />;
        });
      } else {
        return noDataFound();
      }
    }
  };

  const MyTable = (
    <TableContainer elevation={0} component={Paper} sx={sx} className="mytable-parent-common">
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            {props?.data?.headers?.map((item: any) => {
              return (
                <TableCell key={uniqueId(item.id)}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    {item.isCheckBox && (
                      <CheckBox onChange={props.handleSelectedAllScProperty} checked={props.selectAllProperties} />
                    )}

                    <Typography variant={"body2"} color={tokens.Text.General.PrimaryBody}>
                      {" "}
                      {item.content}
                    </Typography>
                    {item?.icon && <item.icon />}
                    {item.isSorting && getSortIcon(item.content)}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={{ ".MuiTableCell-root": { borderBottom: "none" } }}>{getScableContent()}</TableBody>
      </Table>
    </TableContainer>
  );

  if (!isTablePaginated) {
    return MyTable;
  } else {
    return (
      <>
        <Scroller
          scrollableTarget={scrollableTarget}
          dataLength={props?.dataLength || props?.data?.rows?.length || 0}
          loadMore={fetchMore}
          totalList={props.totalRecords}
          label="records"
          footerStyle={{ marginY: "16px" }}
        >
          {MyTable}
        </Scroller>
        {props?.data?.listCategory && props.rowSelection?.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{
              position: "absolute",
              height: "94px",
              backgroundColor: tokens.Background.General.Primary,
              bottom: "-94px",
              right: "0px",
              width: tableTotalWidth,
              paddingLeft: "24px",
              paddingRight: "38px",
              borderTop: `1px solid ${tokens.Border.General.Tertiary}`
            }}
          >
            <div>
              <Button
                variant="secondary"
                disabled={!props.rowSelection?.length}
                label={`Assign Plans (${props.rowSelection?.length})`}
                onClick={() => {
                  if (props.rowSelection.every((item: any) => item.hltId)) {
                    props.setMultiplansAssign(true);
                    dispatch(setSelectedUserList(props.allTeamMember));
                  } else {
                    props.handleSetCheckInncodeUser(true);
                  }
                }}
              />
            </div>
          </Stack>
        )}
      </>
    );
  }
}
